<template>
  <div>
    <form
      v-if="showSearch"
    >
      <div class="relative flex items-center w-2/3 mb-6 form-input sm:w-1/3">
        <Icon
          width="24px"
          height="24px"
          view-box="0 0 24 24"
          class="text-gray-500"
        >
          <Search />
        </Icon>
        <input
          v-model="searchQuery"
          type="text"
          name="query"
          class="w-full ml-3 focus:outline-none"
          placeholder="Search candidates"
        >
      </div>
    </form>

    <table class="w-full text-gray-800 border-collapse">
      <thead>
        <tr>
          <th
            v-for="(column, index) in columns"
            :key="index"
            :class="['uppercase font-normal text-gray-500 text-left text-xs pb-2 px-6 sm:table-cell', [ responsiveColumns(column.key) ? 'table-cell' : 'hidden' ]]"
          >
            <a
              v-if="column.sortable"
              href="javascript:;"
              :class="['flex items-center', { 'font-semibold text-gray-600': column.sortable && (column.key === sortKey && sortOrder === 'asc') }]"
              @click="sortBy(column.key)"
            >
              <span>{{ columnName(column.key) }}</span>

              <div
                v-if="column.sortable"
                class="px-2 text-gray-500"
              >
                <svg
                  :class="['fill-current h-4 w-4', { 'transform rotate-180 text-gray-600': column.key === sortKey && sortOrder === 'asc' }]"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                </svg>
              </div>
            </a>
            <span v-else-if="!column.sortable">
              {{ columnName(column.key) }}
            </span>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="(data, key) in filteredData"
          :key="key"
        >
          <TableCell>
            <CandidateProfileLink
              :candidate-id="data.id"
              class="duration-300 hover:text-secondary"
            >
              <Icon
                width="24px"
                height="24px"
                view-box="0 0 512 512"
                class="mr-4 text-gray-600"
              >
                <CandidateIcon />
              </Icon>
              {{ data.name }}
            </CandidateProfileLink>
          </TableCell>

          <TableCell class="hidden sm:table-cell">
            {{ data.email }}
          </TableCell>

          <TableCell class="hidden sm:table-cell">
            {{ formatDate(data.updatedAt) }}
          </TableCell>

          <TableCell class="hidden sm:table-cell">
            <DeleteCandidateButton
              :candidates="[...data]"
              @candidates-deleted="$emit('refreshCandidates')"
            />
          </TableCell>
        </tr>
      </tbody>
    </table>

    <Pagination
      class="mt-4"
      :total-pages="paginationData.totalPages"
      :per-page="paginationData.perPage"
      :current-page="paginationData.currentPage"
      @pagechanged="$emit('pagechanged', $event)"
    />
  </div>
</template>

<script>
import CandidateProfileLink from '@components/Candidates/CandidateProfileLink'
import Icon from '@components/Icons/Icon'
import CandidateIcon from '@components/Icons/Candidate'
import Pagination from '@components/Pagination'
import Search from '@components/Icons/Search'
import TableCell from '@components/TableCell'
import DeleteCandidateButton from '@components/Candidates/DeleteCandidateButton'

import { truncateString } from '@utils/truncate'
import { formatDate } from '@utils/formatDate'

export default {
  components: {
    CandidateProfileLink,
    Icon,
    CandidateIcon,
    Pagination,
    Search,
    TableCell,
    DeleteCandidateButton
  },

  props: {
    columns: {
      type: Array,
      default: null
    },
    tableData: {
      type: Array,
      default: null
    },
    showSearch: {
      type: Boolean,
      default: false
    },
    icon: {
      type: Boolean,
      default: false
    },
    filter: {
      type: String,
      default: ''
    },
    paginationData: {
      type: Object,
      default: null
    },
    sortData: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      formatDate,
      truncateString,

      searchQuery: '',
      sortKey: 'createdAt',
      sortOrder: 'desc'
    }
  },

  computed: {
    /**
     * Rename the keys to match the column headings
     *
     * @return {Array}
     */
    filteredData() {
      return this.tableData
        .map(({ id, firstName, surname, email, createdAt, updatedAt }) => {
          return {
            id,
            name: `${firstName} ${surname}`,
            email,
            createdAt,
            updatedAt
          }
        })
    }
  },

  created() {
    if (this.sortData) {
      this.sortKey = this.sortData.key
      this.sortOrder = this.sortData.order
    }
  },

  methods: {
    /**
     * Trigger sort by key
     *
     * @param {string} key
     */
    sortBy(key) {
      if (this.sortOrder === 'asc') {
        this.sortOrder = 'desc'
      } else {
        this.sortOrder = 'asc'
      }

      this.$emit('sort', { key, order: this.sortOrder })
    },

    setScoreData(data, slug) {
      const foundationSlug = `foundation-${slug}`
      const attempt = data.attempts.find(attempt => attempt.examSlug === slug || attempt.examSlug === foundationSlug)

      if (!attempt) {
        return '-'
      }

      if (attempt.locked) {
        return '-'
      }

      if (attempt.examType === 'personality') {
        return attempt.result
      } else {
        return attempt.scorePercentage + '%'
      }
    },

    /**
     * Display column headings based on viewport
     */
    responsiveColumns(column) {
      const smallScreenColumns = [
        'name',
        'job'
      ]
      return smallScreenColumns.includes(column)
    },

    /**
     * @param {string} key
     * @return {string}
     */
    columnName(key) {
      const replacements = {
        last_updated: 'Last Updated'
      }
      if (!(key in replacements)) {
        return key
      }
      return replacements[key]
    }
  }
}
</script>
